import { ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { RotateCcw } from 'lucide-react';

import { LoadingDots } from './ui/loading-dots';
import { Button } from './ui/button';

type FetchStatesProps = {
  loading?: boolean;
  loadingMessage?: string;
  error?: string;
  children?: ReactNode;
  empty?: boolean;
  reloadOnError?: boolean;
  refetch?: () => void;
};

export const FetchStates = (props: FetchStatesProps) => {
  const { error, loading, children, empty = false, reloadOnError, refetch, loadingMessage } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleReload = useCallback(() => {
    navigate(0);
  }, [navigate]);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  if (loading) {
    return (
      <section className="flex-full-center flex-col gap-2">
        <LoadingDots />
        {loadingMessage && <p className="text-xs text-gray-900">{loadingMessage}</p>}
      </section>
    );
  }

  if (error) {
    return (
      <section className="h-full flex-full-center flex-col">
        <p className="text-base font-semibold text-red-800">{t('fetch-states-error-title')}</p>
        <h3 className=" text-balance text-3xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
          {error}
        </h3>
        <p className="mt-2 text-pretty text font-medium text-gray-500">
          {t('fetch-states-error-message')}
        </p>
        <div className="mt-10 flex items-center justify-center">
          <Button variant="link" onClick={handleGoBack}>
            {t('go-back')}
          </Button>
          <Button onClick={reloadOnError ? handleReload : refetch}>
            {t('fetch-states-retry')}
            <span aria-hidden="true">
              <RotateCcw />
            </span>
          </Button>
        </div>
      </section>
    );
  }

  if (empty)
    return <div className="flex-full-center text-gray-500">{t('fetch-states-no-data')}</div>;

  return <>{children}</>;
};
